/**
 * src/components/spatial/SpatialObjectExtrusionLayer.tsx
 *
 * Building / catch-all extrusion layer. Renders one fill-extrusion
 * per spatial object whose `objectType` is NOT floor / zone / slice
 * — i.e. building, object_3d, real_estate, event_object, and
 * contract_execution_object all paint here. Paint reads from the
 * canonical GeoLink properties:
 *
 *   - fill-extrusion-base    ← baseHeightMeters (with `base_height` alias fallback)
 *   - fill-extrusion-height  ← heightMeters     (with `height` alias fallback)
 *   - fill-extrusion-color   ← colorKey         (with `fill_color` alias fallback)
 *   - fill-extrusion-opacity ← layer-kind default
 *
 * Buildings stay visible even when a floor is selected so the user
 * keeps a recognisable shell around the floor they're focused on.
 */

import React, { useEffect } from 'react';
import type { SpatialMapProvider } from '../../services/spatial/mapProvider';
import { useSpatialObjects } from './SpatialObjectsContext';
import {
  SPATIAL_LAYER_IDS,
  SPATIAL_SOURCE_ID,
  buildExtrusionBaseExpr,
  buildExtrusionColorExpr,
  buildExtrusionHeightExpr,
  buildExtrusionOpacityExpr,
  buildKindFilter,
} from './spatialLayerUtils';
import { useStrictModeSafeLayer } from './useStrictModeSafeLayer';

export interface SpatialObjectExtrusionLayerProps {
  provider: SpatialMapProvider;
  /** Stable id for the GeoJSON source the parent SpatialObjectsMapLayer creates. */
  sourceId?: string;
  /**
   * Optional layer-id override so multiple instances can co-exist on
   * the same map (rarely needed — the spec calls for one).
   */
  layerId?: string;
  /** Layer to insert before (kept under symbol/text by default). */
  beforeId?: string;
  /** Optional tweak to mute the building shell when a floor is selected. */
  shellOpacityWhenFloorSelected?: number;
}

const SpatialObjectExtrusionLayer: React.FC<SpatialObjectExtrusionLayerProps> = ({
  provider,
  sourceId = SPATIAL_SOURCE_ID,
  layerId  = SPATIAL_LAYER_IDS.building,
  beforeId,
  shellOpacityWhenFloorSelected = 0.18,
}) => {
  const { view, filters } = useSpatialObjects();

  // Mount the fill-extrusion via the StrictMode-safe helper. The build
  // spec callback is invoked at addLayer time so it always sees the
  // latest view/filters; the secondary effect below keeps paint/filter
  // in sync without re-creating the layer (which would flicker).
  useStrictModeSafeLayer({
    provider,
    layerId,
    beforeId,
    buildSpec: () => ({
      id:     layerId,
      type:   'fill-extrusion',
      source: sourceId,
      filter: buildKindFilter('building', view, filters) as unknown[],
      paint:  {
        'fill-extrusion-base':    buildExtrusionBaseExpr(view, 'building'),
        'fill-extrusion-height':  buildExtrusionHeightExpr(view, 'building'),
        'fill-extrusion-color':   buildExtrusionColorExpr('building'),
        'fill-extrusion-opacity': view.selectedFloorNumber != null
          ? shellOpacityWhenFloorSelected
          : buildExtrusionOpacityExpr('building'),
      },
    }),
  });

  // Keep the filter / opacity in sync as the user toggles floor /
  // slice / link filters.
  useEffect(() => {
    if (!provider.hasLayer(layerId)) return;
    provider.setFilter(layerId, buildKindFilter('building', view, filters) as unknown[]);
    provider.setPaintProperty(layerId, 'fill-extrusion-opacity',
      view.selectedFloorNumber != null
        ? shellOpacityWhenFloorSelected
        : buildExtrusionOpacityExpr('building'));
    provider.setPaintProperty(layerId, 'fill-extrusion-base',   buildExtrusionBaseExpr(view, 'building'));
    provider.setPaintProperty(layerId, 'fill-extrusion-height', buildExtrusionHeightExpr(view, 'building'));
  }, [provider, layerId, view, filters, shellOpacityWhenFloorSelected]);

  return null;
};

export default SpatialObjectExtrusionLayer;
